import {
  createFakeCategoryTree,
  getCollectionById,
  KuxDms,
  KuxFilteredCategoriesDTO,
  normalizeCategoryMetas,
  PresentationAdditionalTypeEnum,
  UIDAPresentationTypeEnum,
} from '@kaltura-ott/tvpil';
import { lastValueFrom } from 'rxjs';

import { RailTypes } from '../../entities';
import type { GridPage } from '../aside';
import type { CategoryChildren } from '../category-children';
import {
  CollectionRailStoreV2,
  ContinueRailStoreV2,
  HeroRailStoreV2,
  RailStoreV2,
  RecommendationsRailStoreV2,
  SeriesRailStoreV2,
} from '../widgets/rails';
import { RentalsRailStoreV2 } from '../widgets/rails/rentals-rail/rentals-rail-store-v2';

export const creatorRailStore = (
  subtype: UIDAPresentationTypeEnum | PresentationAdditionalTypeEnum,
  categoryTree: KuxFilteredCategoriesDTO,
  parentCategoryTree: KuxFilteredCategoriesDTO = createFakeCategoryTree(),
): RailTypes => {
  switch (subtype) {
    case UIDAPresentationTypeEnum.collection:
      return new CollectionRailStoreV2(categoryTree, parentCategoryTree);
    case UIDAPresentationTypeEnum.cw:
      return new ContinueRailStoreV2(categoryTree, parentCategoryTree);
    case UIDAPresentationTypeEnum.suggested:
      return new RecommendationsRailStoreV2(categoryTree, parentCategoryTree);
    case UIDAPresentationTypeEnum.series_catch_up:
      return new SeriesRailStoreV2(categoryTree, parentCategoryTree);
    case UIDAPresentationTypeEnum.hero:
      return new HeroRailStoreV2(categoryTree, parentCategoryTree);
    case UIDAPresentationTypeEnum.tvod:
      return new RentalsRailStoreV2(categoryTree, parentCategoryTree);
    default:
      throw new Error(`${subtype} - wrong rail`);
  }
};

export const createGridRail = async (
  gridPage: GridPage,
  rootChildren: CategoryChildren[],
  targetId: number | string,
  dms: KuxDms,
): Promise<RailTypes | undefined> => {
  let rail = rootChildren
    .flatMap((child) => child.children)
    .find(({ referenceId }) => referenceId === targetId.toString()) as RailStoreV2 | undefined;

  const isGroupedGrid = dms.GridCharacteristics?.GRID_TYPES.includes(gridPage.referenceId);
  const groupByDMS = isGroupedGrid ? dms.GridCharacteristics?.GROUP_BY_VALUE : '';

  if (!rail) {
    const kalturaChannel = await lastValueFrom(getCollectionById(Number(targetId)));
    const kalturaChannelMetas = normalizeCategoryMetas(kalturaChannel.metaData);
    rail = {
      subtype: UIDAPresentationTypeEnum.collection,
      categoryTree: {
        type: 'widget',
        subtype: 'collection',
        id: 9999999, // random id
        name: kalturaChannel.name,
        anchors: [],
        images: [],
        channels: [Number(targetId)], // consider targetId is a collection id if we are here
        parentalPinProtected: false,
        presentPlayer: false,
        groupBy: groupByDMS,
        aggregatedKSQL:
          (kalturaChannelMetas?.FilterByEntitlementRequired as unknown as string)?.toLowerCase() === 'true'
            ? `entitled_assets='entitledSubscriptions'`
            : '',
        uiDesignElements: {},
        railImageProperty: {},
        link: {},
        railProperty: {
          presentEntitlementIndicator: false,
          presentShowMore: true,
          presentCurrentPrograms: false,
          railItemsLimit: 0,
          presentRating: false,
          ratingProvider: '',
          presentRatingSourceLogo: false,
          programRefreshFrequency: 300,
          showTopShelfRailUi: false,
          isGroupedGrid,
        },
        subpageProperty: {},
        epgProperty: {
          filters: [],
          presentationRatio: '16x9',
          assetPresentationImageType: 308,
        },
        assetDetailsPageProperty: {
          struct: 'undefined',
          presentationRatio: '16x9',
          assetPresentationImageType: 308,
          imageHeight: 200,
          imageWidth: 0,
          rentalBadgeBoundary: 0,
        },
        asideProperty: {},
        continueWatchingProperty: {},
        children: [],
        channelApiSearchMeta: {},
        mostWatched: {},
        searchProperty: {},
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } as any;
  }

  const categoryTree = {
    ...rail?.categoryTree,
    groupBy: rail?.groupBy ? rail?.groupBy : groupByDMS,
    asideProperty: {
      presentEntitlementIndicator: gridPage.presentEntitlementIndicator,
      presentFavoriteIndicator: gridPage.presentFavoriteIndicator,
      presentRating: gridPage.presentRating,
      ratingProvider: gridPage.ratingProvider,
      presentRatingSourceLogo: gridPage.presentRatingSourceLogo,
    },
    assetDetailsPageProperty: {
      ...rail?.categoryTree.assetDetailsPageProperty,
      presentationRatio: gridPage.ratioForAllItems,
      imageHeight: gridPage.imageHeight,
      assetPresentationImageType: gridPage.assetPresentationImageType,
    },
    railImageProperty: {
      assetImageHeight: gridPage.imageHeight,
      assetPresentationRatio: gridPage.ratioForAllItems,
      assetPresentationImageType: gridPage.assetPresentationImageType,
    },
    parentalPinProtected: gridPage.parentalPinProtected,
    railProperty: {
      ...rail?.categoryTree.railProperty,
      railItemsLimit: undefined,
      presentRating: gridPage.presentRating,
      ratingProvider: gridPage.ratingProvider,
      presentRatingSourceLogo: gridPage.presentRatingSourceLogo,
      presentEntitlementIndicator: gridPage.presentEntitlementIndicator,
      isGroupedGrid,
    },
    imageScreens: {
      ...rail?.categoryTree.imageScreens,
      imageHeight: gridPage.imageHeight,
      imageHeightMedium: gridPage.imageHeightMedium,
      imageHeightCompact: gridPage.imageHeightCompact,
    },
    uiDesignElements: gridPage.uiDesignElements,
  } as KuxFilteredCategoriesDTO;

  if (!rail) {
    return;
  }

  return creatorRailStore(rail.subtype, categoryTree, rail.parentCategoryTree);
};
